import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, Truck, Printer, ShieldCheck } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { fmt } from "../lib/pricing";
import { ProductImg } from "../components/ProductImg";

export default function OrderSuccess() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const location = useLocation();
  const orderNumber = params.get("n") || "—";
  const order = location.state?.order;
  const shipping = location.state?.shippingForm;

  const paymentLabel = {
    card: t.checkout.card,
    paypal: t.checkout.paypal,
    apple_pay: t.checkout.applePay,
  }[order?.payment_method] || t.checkout.card;

  return (
    <main
      data-testid="order-success-page"
      className="bg-black min-h-screen pt-28 md:pt-32 pb-20"
    >
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-400 text-black mb-8">
            <CheckCircle2 className="w-8 h-8" strokeWidth={1.6} />
          </div>
          <p className="overline mb-4">{t.success.overline}</p>
          <h1 className="h-display text-white text-5xl md:text-6xl">
            {t.checkout.success}
          </h1>
          <p className="text-white/60 mt-6 leading-relaxed max-w-md mx-auto">
            {t.checkout.successCopy}
          </p>

          <div className="mt-10 inline-flex flex-col border border-white/10 px-8 py-5">
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">
              {t.checkout.orderNumber}
            </span>
            <span
              data-testid="success-order-number"
              className="font-display text-2xl text-yellow-400 mt-1"
            >
              {orderNumber}
            </span>
          </div>
        </div>

        {order && (
          <div className="border border-white/10 p-6 md:p-8 mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="overline">{t.success.itemsOrdered}</h2>
              <button
                onClick={() => window.print()}
                data-testid="print-receipt"
                className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-yellow-400"
              >
                <Printer className="w-4 h-4" /> {t.success.printReceipt}
              </button>
            </div>

            <ul className="space-y-4">
              {order.items?.map((i, idx) => (
                <li
                  key={idx}
                  data-testid={`success-item-${idx}`}
                  className="flex gap-4 pb-4 border-b border-white/10 last:border-b-0"
                >
                  <div className="w-16 h-20 bg-zinc-900 overflow-hidden flex-shrink-0">
                    {i.image && (
                      <ProductImg src={i.image} alt="" className="w-full h-full object-cover" small />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold uppercase">{i.name}</p>
                    <p className="text-[11px] text-white/50 uppercase tracking-widest mt-1">
                      {i.size && <>{i.size} · </>}
                      {i.color && <>{i.color} · </>}
                      {t.cart.quantity} {i.quantity}
                    </p>
                  </div>
                  <p className="text-sm font-bold">${(i.price * i.quantity).toFixed(2)}</p>
                </li>
              ))}
            </ul>

            <div className="mt-6 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-white/60">{t.cart.subtotal}</span>
                <span>{fmt(order.subtotal)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-yellow-400">
                  <span>
                    {t.cart.discount}
                    {order.coupon_code && <> · {order.coupon_code}</>}
                  </span>
                  <span>-{fmt(order.discount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-white/60">{t.cart.shipping}</span>
                <span>
                  {order.shipping === 0 ? (
                    <span className="text-yellow-400 font-bold uppercase text-xs tracking-widest">
                      {t.cart.free}
                    </span>
                  ) : (
                    fmt(order.shipping)
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">{t.cart.tax}</span>
                <span>{fmt(order.tax)}</span>
              </div>
              <div className="flex justify-between font-display text-xl uppercase pt-3 border-t border-white/10 mt-3">
                <span>{t.cart.total}</span>
                <span data-testid="success-total">{fmt(order.total)}</span>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6 border-t border-white/10">
              {shipping && (
                <div>
                  <p className="overline flex items-center gap-2 mb-3">
                    <Truck className="w-3 h-3" /> {t.success.shipTo}
                  </p>
                  <p className="text-sm text-white/80" data-testid="success-shipping">
                    {shipping.full_name}
                    <br />
                    {shipping.address}
                    <br />
                    {shipping.city}, {shipping.state} {shipping.postal_code}
                    <br />
                    {shipping.country}
                    <br />
                    {shipping.phone}
                  </p>
                </div>
              )}
              <div>
                <p className="overline flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-3 h-3" /> {t.success.paidWith}
                </p>
                <p className="text-sm text-white/80" data-testid="success-payment-method">
                  {paymentLabel}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-yellow-400 text-black px-8 py-4 font-bold uppercase tracking-[0.2em] text-xs hover:bg-yellow-300 transition-colors"
          >
            {t.checkout.backToShop}
          </Link>
        </div>
      </div>
    </main>
  );
}
